import { ModuleRendering, SlotRendering, isSlotRenderingRenderable } from '@frontend/common-types';
import React from 'react';

export interface ModuleSlotListProps<TModule extends ModuleRendering = ModuleRendering, TSlot extends SlotRendering = SlotRendering> {
    modules: TModule[];
    onSlotClick?: (clickedSlot: TSlot) => void;
    slotRenderElement?: (slot: TSlot) => React.ReactNode;
}

const slotLabel = (slot: SlotRendering): string => {
    if (slot.display) return String(slot.display);
    if (slot.number != null && slot.number !== '') return String(slot.number);
    if (slot.index != null) return String(slot.index);
    return slot.id;
};

/**
 * Selectable SLOT grid for non-renderable (virtual) modules — not geometric SpotLayout.
 * Prefer `slotRenderElement` so screens keep select/disabled behavior (e.g. SlotLayoutCell).
 */
export const ModuleSlotList = <TModule extends ModuleRendering = ModuleRendering, TSlot extends SlotRendering = SlotRendering>(
    props: ModuleSlotListProps<TModule, TSlot>
) => {
    const slots = props.modules.flatMap((module) => (module.slots ?? []).filter(isSlotRenderingRenderable)) as TSlot[];

    return (
        <div
            data-testid='module-slot-list'
            className='grid w-full grid-cols-[repeat(auto-fill,minmax(5.5rem,1fr))] gap-2 self-stretch'>
            {slots.map((slot) => (
                <div
                    key={slot.id}
                    data-testid={`module-slot-list-cell-${slot.id}`}
                    className='aspect-square min-h-0 w-full overflow-hidden rounded-xl'>
                    {props.slotRenderElement ? (
                        props.slotRenderElement(slot)
                    ) : (
                        <button
                            type='button'
                            className='border-border-default bg-background-surface text-foreground-default flex size-full items-end p-2 text-left text-sm font-semibold'
                            onClick={() => props.onSlotClick?.(slot)}>
                            {slotLabel(slot)}
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
};
